import { FC, useState } from 'react';

import { Box, Checkbox, FormControlLabel, IconButton, Stack, TextField, Tooltip } from '@mui/material';

import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';
import { TrblCrankWheelIcon } from '@/components/Icons';
import { TrblPopup, TrblPopupActions, TrblPopupContent, TrblPopupTitle } from '@/components/Shared/Popup';

import { useUpdateOrganizationSettings } from '../hooks';

import { OrganizationResponse } from '../types';

import styles from '../styles.module.scss';

type OrganizationDetailsProps = {
  organization?: OrganizationResponse;
};

export const OrganizationDetails: FC<OrganizationDetailsProps> = ({ organization }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [shareAnonymizedData, setShareAnonymizedData] = useState(true);
  const { mutate: updateOrganizationSettings, isPending } = useUpdateOrganizationSettings();

  const openSettings = () => {
    setShareAnonymizedData(organization?.organization.shareAnonymizedData ?? true);
    setShowSettings(true);
  };

  const saveSettings = () => {
    if (organization) {
      updateOrganizationSettings(
        { organizationId: organization.organization.id, shareAnonymizedData },
        { onSuccess: () => setShowSettings(false) }
      );
    }
  };

  return (
    <Box component="div" display="flex" flexDirection="column" gap="6px" height="100%">
      <Box component="div" display="flex" justifyContent="space-between" alignItems="center">
        <p style={{ fontWeight: '600' }}>Organization details</p>
        <Tooltip title="Organization settings">
          <IconButton
            onClick={openSettings}
            aria-label="Organization settings"
            size="small"
            sx={{ marginBottom: '-2px' }}>
            <TrblCrankWheelIcon width="16" height="16" />
          </IconButton>
        </Tooltip>
      </Box>
      {showSettings && (
        <TrblPopup open width="600px">
          <TrblPopupTitle onClose={() => setShowSettings(false)}>Organization Settings</TrblPopupTitle>
          <TrblPopupContent>
            <FormControlLabel
              control={
                <Checkbox checked={shareAnonymizedData} onChange={(e) => setShareAnonymizedData(e.target.checked)} />
              }
              label="Share anonymized data to enhance the Treble Platform's capabilities (Sec. 6.2.)"
              sx={{ '.MuiFormControlLabel-label': { fontSize: 12 } }}
            />
          </TrblPopupContent>
          <TrblPopupActions>
            <SecondaryButton label="Cancel" onClick={() => setShowSettings(false)} width={150} />
            <PrimaryButton label="Save" onClick={saveSettings} disabled={isPending} width={150} />
          </TrblPopupActions>
        </TrblPopup>
      )}
      <Box component="div" display="flex" flex="1 1 0" bgcolor="#272727" borderRadius="4px">
        <Stack spacing={1.8} padding="24px 10px 12px" flexGrow={1}>
          <TextField
            className={styles['text-field-input']}
            InputProps={{ disabled: true }}
            label="Name"
            value={organization?.organization.name || ''}
          />
          <TextField
            className={styles['text-field-input']}
            InputProps={{ disabled: true }}
            label="Description"
            multiline
            minRows={2}
            value={organization?.organization.description || ''}
          />
        </Stack>
      </Box>
    </Box>
  );
};
